import { Injectable, inject } from '@angular/core';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { CallbackId, DocumentData } from '@capacitor-firebase/firestore';
import { Subscription } from 'rxjs';
import { HistoryRepository } from 'src/app/analytics/state/history.repository';
import { RemoteUserSettings, UserSettings } from 'src/app/settings/state/settings.interface';
import { SettingsRepository } from 'src/app/settings/state/settings.repository';
import { TimestampEntity } from 'src/app/timer/state/timer.interface';
import { dayKeyFor } from 'src/app/utilities/day-key.utils';
import {
  itemsEqual,
  LocalDayState,
  RemoteDayDoc,
  resolveDay,
} from 'src/app/utilities/sync-arbitration.utils';
import { FirestoreApiService } from '../firebase/firestore-api.service';

const WRITER_ID_KEY = 'pf-timer-writer-id';
const FORK_BACKUPS_KEY = 'pf-timer-fork-backups';
const FORK_BACKUP_CAP = 10;
// Separate, uncapped bucket for cross-account quarantine bursts — see pushQuarantineBackups.
const QUARANTINE_BACKUPS_KEY = 'pf-timer-quarantine-backups';
const SAVE_DEBOUNCE_MS = 500;
const ROLLOVER_INTERVAL_MS = 60_000;
const RETRY_DELAYS_MS = [1000, 5000, 30000];

/** Day document written by this engine (v2 schema). */
interface DayDocV2 {
  date: string;
  items: TimestampEntity[];
  metadata: { goal: number | null; multiplier: number };
  revision: number;
  modifiedAt: number;
  writerId: string;
}

/** The losing side of an arbitrated fork, stashed to localStorage for manual recovery. */
interface ForkBackup {
  date: string;
  stashedAt: number;
  source: 'local' | 'remote';
  items: TimestampEntity[];
  goal: number | null;
  multiplier: number;
}

/** Minimal shape of the plugin's document snapshot the engine reads from. */
interface DocSnapshot {
  id?: string;
  data: DocumentData | null;
}

/**
 * Revision-arbitrated per-day sync engine.
 *
 * Owns the Firestore side effects for keeping a device's timer log in sync across installs.
 * All winner/loser decisions are delegated to `resolveDay` (see `sync-arbitration.utils`); this
 * service never re-implements them. Days are replaced wholesale — entries are never unioned.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseSyncService {
  private firestore = inject(FirestoreApiService);
  private history = inject(HistoryRepository);
  private settings = inject(SettingsRepository);

  private uid: string | null = null;
  private writerId: string | null = null;

  // Monotonic lifecycle token. Bumped on every stop() so any async work (in-flight saves, pending
  // plugin-listener handles) started under an earlier generation can detect it must not act.
  private generation = 0;

  private listenerCallbackId: CallbackId | null = null;
  private listenerDateKey: string | null = null;
  private lastRemoteToday: RemoteDayDoc | null = null;

  private saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private saveInFlight = false;
  // Dates that requested a save while one was in flight. Tracks WHICH date(s) need a follow-up
  // cycle (not just "some rerun"), so a rollover-attached new date is never dropped.
  private pendingSaveDates = new Set<string>();

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryIndex = 0;
  private retryPending = false;
  private retryDateKey: string | null = null;

  private rolloverTimer: ReturnType<typeof setInterval> | null = null;
  private backfilling = false;

  private onlineHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private appStateListener: PluginListenerHandle | null = null;

  // Settings sync (last-writer-wins). The listener mirrors remote->local; the subscription mirrors
  // local->remote. Both are attached in start(uid), torn down in stop(), generation-guarded.
  private settingsListenerCallbackId: CallbackId | null = null;
  private settingsSubscription: Subscription | null = null;

  // ---- lifecycle -----------------------------------------------------------

  async start(uid: string): Promise<void> {
    if (this.uid === uid) {
      return;
    }
    if (this.uid) {
      await this.stop();
    }
    this.uid = uid;
    this.quarantineForeignOwnedDays(uid);
    try {
      await this.attachListener(uid, this.todayKey());
    } catch (error) {
      // Never leave the engine half-started with uid set — a later start(sameUid) would no-op and
      // the engine would never actually attach. Roll the partial start back, then surface the error.
      console.error('Sync failed to attach today listener; rolling back start', error);
      await this.stop();
      throw error;
    }
    this.registerReactivationHooks();
    this.startRolloverWatcher();
    await this.attachSettingsSync(uid);
    await this.backfillHistory(uid);
  }

  async stop(): Promise<void> {
    // Invalidate any in-flight async work first (an in-flight save that resolves after this must
    // not setDocument/markSynced or schedule a retry), then tear down all local timers/hooks/state
    // BEFORE the async listener removal so a plugin rejection can never abort the rest of cleanup.
    this.generation++;
    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    }
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.rolloverTimer !== null) {
      clearInterval(this.rolloverTimer);
      this.rolloverTimer = null;
    }
    this.unregisterReactivationHooks();
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
      this.settingsSubscription = null;
    }
    this.saveInFlight = false;
    // Release the backfill guard too, so a rapid stop()/start() runs a fresh backfill instead of
    // being blocked by a stale in-flight backfill that the generation bump has already neutered.
    this.backfilling = false;
    this.pendingSaveDates.clear();
    this.retryPending = false;
    this.retryIndex = 0;
    this.retryDateKey = null;
    this.lastRemoteToday = null;
    this.listenerDateKey = null;
    this.uid = null;
    await this.detachListener();
    await this.detachSettingsListener();
  }

  /**
   * Guards against a day surviving sign-out (clearSyncedDays deliberately keeps anything not
   * provably synced — dirty !== false — so purely local data is never destroyed, see
   * HistoryRepository.clearSyncedDays) from being attributed to a DIFFERENT account on the next
   * sign-in. Without this, backfillHistory would treat account A's surviving unsynced days as
   * account B's local-only data and upload them straight into B's Firestore (C2 fix).
   *
   * `lastSyncedUid` (see SettingsRepository) is the ownership signal: stamped on every successful
   * start(uid), read here BEFORE it advances. A day is only quarantined when a DIFFERENT uid was
   * previously recorded — first-ever sign-in on a device (no prior owner) leaves local-only
   * ("guest") data untouched, preserving the existing local-first-then-sync-on-login behaviour.
   * Quarantined days are stashed to a dedicated, uncapped backup bucket (never destroyed outright,
   * see pushQuarantineBackups) and removed locally so backfillHistory never sees them.
   *
   * This is a coarse, device-wide signal rather than a per-day owner (no per-entity uid field): the
   * device's ENTIRE surviving local history is attributed to whichever account last completed a
   * start(uid) here. The one accepted false-positive is local-only ("guest") activity created
   * WHILE signed out, in between two different accounts' sessions on the same device — it gets
   * conservatively quarantined (stashed, not lost) rather than risked leaking into the new
   * account. Chosen over a per-entity uid stamp (which would need every local mutation call site —
   * TimestampService, TimerService, addSettings, etc. — updated in lockstep) as the simplest fix
   * that closes the actual reported leak without that much larger surface area.
   */
  private quarantineForeignOwnedDays(uid: string): void {
    const previousOwner = this.settings.getLastSyncedUid();
    if (previousOwner && previousOwner !== uid) {
      const foreignDayIds: string[] = [];
      const quarantined: ForkBackup[] = [];
      for (const entity of this.history.getEntries()) {
        if ((entity.timestamps?.length ?? 0) === 0 && entity.goal == null) {
          continue; // nothing to leak
        }
        const local = this.buildLocalDayState(entity.id);
        if (local) {
          quarantined.push(this.toForkBackup(entity.id, 'local', local));
        }
        foreignDayIds.push(entity.id);
      }
      // A single burst write — see pushQuarantineBackups for why this must never go through the
      // shared, capped pushForkBackup path.
      this.pushQuarantineBackups(quarantined);
      this.history.removeDays(foreignDayIds);
    }
    this.settings.setLastSyncedUid(uid);
  }

  /** Lazily created, per-install id persisted to localStorage. Always non-empty. */
  getWriterId(): string {
    if (this.writerId) {
      return this.writerId;
    }
    let id: string | null = null;
    try {
      id = localStorage.getItem(WRITER_ID_KEY);
    } catch {
      // read failure (private mode/quota) — fall through and mint a fresh id
    }
    if (!id) {
      id = this.mintWriterId();
      try {
        localStorage.setItem(WRITER_ID_KEY, id);
      } catch {
        // persist failure — keep the in-memory id for this session
      }
    }
    this.writerId = id;
    return id;
  }

  // ---- snapshot handling ---------------------------------------------------

  private onTodaySnapshot(snapshot: DocSnapshot | null, error: unknown): void {
    if (error) {
      console.error('Sync snapshot listener error', error);
      return;
    }
    if (!this.uid || !this.listenerDateKey) {
      return;
    }
    const dateKey = snapshot?.id ?? this.listenerDateKey;
    if (dateKey !== this.listenerDateKey) {
      return; // stale event from a listener that has since rolled over
    }
    const remote = this.toRemoteDayDoc(dateKey, snapshot?.data ?? null);
    this.lastRemoteToday = remote;
    const local = this.buildLocalDayState(dateKey);
    const resolution = resolveDay(local, remote);

    switch (resolution.action) {
      case 'none':
        return;
      case 'adopt':
        this.adoptRemoteMaybeStashing(dateKey, local, remote);
        return;
      case 'write':
        this.scheduleSaveToday();
        return;
      case 'fork-local-wins':
        // The losing remote is stashed by the serialized save (avoids double-stashing).
        this.scheduleSaveToday();
        return;
      case 'fork-remote-wins':
        this.stashLocalFork(dateKey, local);
        this.adoptRemote(dateKey, remote);
        return;
    }
  }

  // ---- saving --------------------------------------------------------------

  /**
   * Public entry point for local mutations: debounce a save of `dateKey`. Backdated entries land on
   * a day the listener is not attached to, so the changed day — not "today" — must be scheduled.
   * No-op until the engine is started, so pre-login callers are safe.
   */
  scheduleSaveDay(dateKey: string): void {
    if (!this.uid) {
      return;
    }
    this.pendingSaveDates.add(dateKey);
    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
    }
    this.saveDebounceTimer = setTimeout(() => {
      this.saveDebounceTimer = null;
      const next = this.takeNextPendingSave();
      if (next !== null) {
        void this.saveTodayNow(next);
      }
    }, SAVE_DEBOUNCE_MS);
  }

  scheduleSaveToday(): void {
    this.scheduleSaveDay(this.todayKey());
  }

  /** Serialized read-resolve-write cycle. Never lets two cycles interleave. */
  private async saveTodayNow(dateKey: string): Promise<void> {
    if (this.saveInFlight) {
      // Record the exact date that requested a rerun so every requested date gets its own cycle
      // (at rollover the in-flight cycle is the OLD date and the follow-up must be the NEW date).
      this.pendingSaveDates.add(dateKey);
      return;
    }
    const gen = this.generation;
    this.saveInFlight = true;
    try {
      await this.runSaveCycle(dateKey, gen);
      if (gen === this.generation) {
        this.resetRetry();
      }
    } catch (error) {
      if (gen === this.generation) {
        console.error('Sync save failed', error);
        this.scheduleRetry(dateKey);
      }
    } finally {
      // Only clear the flag for the LIVE generation. A stale save resolving after a stop()/restart
      // must not clear a flag a fresh save (new generation) is relying on, or two live cycles could
      // interleave.
      if (gen === this.generation) {
        this.saveInFlight = false;
      }
    }
    if (gen !== this.generation) {
      // Engine stopped/restarted mid-save: stop() already cleared pending state, and the live
      // generation may have parked new dates — a stale save must not touch them.
      return;
    }
    const next = this.takeNextPendingSave();
    if (next !== null) {
      await this.saveTodayNow(next);
    }
  }

  /** Pulls one pending rerun date (insertion order) out of the set, or null when drained. */
  private takeNextPendingSave(): string | null {
    const result = this.pendingSaveDates.values().next();
    if (result.done) {
      return null;
    }
    const dateKey = result.value;
    this.pendingSaveDates.delete(dateKey);
    return dateKey;
  }

  private async runSaveCycle(dateKey: string, gen: number): Promise<void> {
    const uid = this.uid;
    if (!uid) {
      return;
    }
    const local = this.buildLocalDayState(dateKey);
    const remote = await this.resolveBaseRemote(uid, dateKey);
    if (gen !== this.generation) {
      return; // engine stopped during the base read — abort before any side effect
    }
    const resolution = resolveDay(local, remote);

    switch (resolution.action) {
      case 'none':
        return;
      case 'adopt':
        this.adoptRemoteMaybeStashing(dateKey, local, remote);
        return;
      case 'fork-remote-wins':
        this.stashLocalFork(dateKey, local);
        this.adoptRemote(dateKey, remote);
        return;
      case 'fork-local-wins':
        // Stash the losing remote only AFTER the write lands: a failing/retried write throws before
        // this line, so the loser is stashed exactly once (and stays in the cloud until we win).
        await this.writeDay(uid, dateKey, local as LocalDayState, resolution.revision, gen);
        this.stashRemoteFork(dateKey, remote);
        return;
      case 'write':
        await this.writeDay(uid, dateKey, local as LocalDayState, resolution.revision, gen);
        return;
    }
  }

  /** Base document for a resolve: the cached today doc when fresh, otherwise a live read. */
  private async resolveBaseRemote(uid: string, dateKey: string): Promise<RemoteDayDoc | null> {
    if (
      this.lastRemoteToday &&
      this.lastRemoteToday.date === dateKey &&
      dateKey === this.listenerDateKey
    ) {
      return this.lastRemoteToday;
    }
    const result = await this.firestore.getDocument<DocumentData>(this.dayRef(uid, dateKey));
    return this.toRemoteDayDoc(dateKey, result?.snapshot?.data ?? null);
  }

  private async writeDay(
    uid: string,
    dateKey: string,
    local: LocalDayState,
    revision: number,
    gen: number,
  ): Promise<void> {
    if (gen !== this.generation) {
      return; // engine stopped before the write was issued — do not touch Firestore or the store
    }
    const doc: DayDocV2 = {
      date: dateKey,
      items: local.items,
      metadata: { goal: local.goal, multiplier: local.multiplier },
      revision,
      modifiedAt: Date.now(),
      writerId: this.getWriterId(),
    };
    await this.firestore.setDocument(this.dayRef(uid, dateKey), doc, false);
    if (gen !== this.generation) {
      return; // engine stopped during the write — do not markSynced or refresh the cache
    }
    // Compare-and-clear: pass the modifiedAt of the state we just serialized. If a user mutation
    // landed during the await, the entity's modifiedAt has advanced and markSynced preserves dirty,
    // so the parked pendingSaveDates rerun uploads the newer items instead of the cloud silently
    // keeping this truncated version forever.
    this.history.markSynced(dateKey, revision, local.modifiedAt);
    if (dateKey === this.listenerDateKey) {
      this.lastRemoteToday = {
        date: doc.date,
        items: doc.items,
        metadata: doc.metadata,
        revision: doc.revision,
        modifiedAt: doc.modifiedAt,
        writerId: doc.writerId,
      };
    }
  }

  // ---- write-failure retry -------------------------------------------------

  private scheduleRetry(dateKey: string): void {
    this.retryPending = true;
    this.retryDateKey = dateKey;
    const delay = RETRY_DELAYS_MS[Math.min(this.retryIndex, RETRY_DELAYS_MS.length - 1)];
    this.retryIndex++;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
    }
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.saveTodayNow(dateKey);
    }, delay);
  }

  private resetRetry(): void {
    this.retryPending = false;
    this.retryIndex = 0;
    this.retryDateKey = null;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  // ---- backfill ------------------------------------------------------------

  /**
   * Reconciles every known day (local ∪ remote) once. Re-entrancy is guarded and the guard is
   * always released via try/finally, even on error. Writes are sequential (no batch API).
   */
  async backfillHistory(uid: string): Promise<void> {
    if (this.backfilling) {
      return;
    }
    this.backfilling = true;
    const gen = this.generation;
    try {
      const result = await this.firestore.getCollection<DocumentData>(`timer/${uid}/entries`);
      const remoteByDate = new Map<string, RemoteDayDoc>();
      for (const snap of result?.snapshots ?? []) {
        const remote = this.toRemoteDayDoc(snap.id, snap.data);
        if (remote) {
          remoteByDate.set(snap.id, remote);
        }
      }

      const dateKeys = new Set<string>();
      for (const entity of this.history.getEntries()) {
        dateKeys.add(entity.id);
      }
      for (const key of remoteByDate.keys()) {
        dateKeys.add(key);
      }

      for (const dateKey of dateKeys) {
        if (gen !== this.generation) {
          break; // engine stopped/restarted during backfill — abandon the remaining days
        }
        if (dateKey === this.listenerDateKey) {
          // Today is owned by the live listener, which may have advanced its revision after this
          // backfill's snapshot was captured. Route it through the serialized save path so it
          // re-reads the freshest remote and can never revert today from the stale snapshot.
          await this.saveTodayNow(dateKey);
        } else {
          await this.backfillDay(uid, dateKey, remoteByDate.get(dateKey) ?? null, gen);
        }
      }
    } catch (error) {
      console.error('Sync backfill failed', error);
    } finally {
      // A stale backfill (superseded by stop()/restart) must not clear the guard a fresh backfill
      // under the new generation now owns.
      if (gen === this.generation) {
        this.backfilling = false;
      }
    }
  }

  private async backfillDay(
    uid: string,
    dateKey: string,
    remote: RemoteDayDoc | null,
    gen: number,
  ): Promise<void> {
    const local = this.buildLocalDayState(dateKey);
    const resolution = resolveDay(local, remote);

    switch (resolution.action) {
      case 'none':
        return;
      case 'adopt':
        this.adoptRemoteMaybeStashing(dateKey, local, remote);
        return;
      case 'fork-remote-wins':
        this.stashLocalFork(dateKey, local);
        this.adoptRemote(dateKey, remote);
        return;
      case 'fork-local-wins':
        // Stash the losing remote only after the write lands (exactly-once, crash-safe).
        await this.writeDay(uid, dateKey, local as LocalDayState, resolution.revision, gen);
        this.stashRemoteFork(dateKey, remote);
        return;
      case 'write':
        await this.writeDay(uid, dateKey, local as LocalDayState, resolution.revision, gen);
        return;
    }
  }

  // ---- day rollover --------------------------------------------------------

  private startRolloverWatcher(): void {
    this.rolloverTimer = setInterval(() => this.checkRollover(), ROLLOVER_INTERVAL_MS);
  }

  private checkRollover(): void {
    if (!this.uid || !this.listenerDateKey) {
      return;
    }
    const current = this.todayKey();
    if (current === this.listenerDateKey) {
      return;
    }
    const oldDate = this.listenerDateKey;
    // Claim the new date synchronously so a re-fired watcher does not roll over twice.
    this.listenerDateKey = current;
    this.lastRemoteToday = null;
    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
      this.saveDebounceTimer = null;
    }
    void this.performRollover(oldDate, current);
  }

  private async performRollover(oldDate: string, newDate: string): Promise<void> {
    await this.saveTodayNow(oldDate); // immediate, non-debounced flush of the old day
    await this.detachListener();
    if (this.uid && this.listenerDateKey === newDate) {
      await this.attachListener(this.uid, newDate);
    }
  }

  // ---- reactivation hooks --------------------------------------------------

  private registerReactivationHooks(): void {
    this.onlineHandler = () => this.onReactivate();
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.onReactivate();
      }
    };
    window.addEventListener('online', this.onlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
    // App.addListener resolves asynchronously; capture the generation so a handle from a
    // superseded start (stopped/restarted before it resolved) is removed instead of overwriting
    // and leaking the current field.
    const gen = this.generation;
    this.addAppStateListener()
      .then((handle) => {
        if (gen === this.generation && !this.appStateListener) {
          this.appStateListener = handle;
        } else {
          void handle.remove();
        }
      })
      .catch((error) => console.error('appStateChange listener failed', error));
  }

  /** Isolated seam over the Capacitor plugin (whose proxy can't be spied) so tests can stub it. */
  private addAppStateListener(): Promise<PluginListenerHandle> {
    return App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.onReactivate();
      }
    });
  }

  private unregisterReactivationHooks(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.appStateListener) {
      void this.appStateListener.remove();
      this.appStateListener = null;
    }
  }

  private onReactivate(): void {
    this.checkRollover();
    if (this.retryPending && this.retryDateKey) {
      if (this.retryTimer !== null) {
        clearTimeout(this.retryTimer);
        this.retryTimer = null;
      }
      void this.saveTodayNow(this.retryDateKey);
    }
  }

  // ---- fork backups --------------------------------------------------------

  private toForkBackup(
    dateKey: string,
    source: ForkBackup['source'],
    day: { items: TimestampEntity[]; goal: number | null; multiplier: number },
  ): ForkBackup {
    return {
      date: dateKey,
      stashedAt: Date.now(),
      source,
      items: day.items,
      goal: day.goal,
      multiplier: day.multiplier,
    };
  }

  private stashLocalFork(dateKey: string, local: LocalDayState | null): void {
    if (!local) {
      return;
    }
    this.pushForkBackup(this.toForkBackup(dateKey, 'local', local));
  }

  private stashRemoteFork(dateKey: string, remote: RemoteDayDoc | null): void {
    if (!remote) {
      return;
    }
    this.pushForkBackup(
      this.toForkBackup(dateKey, 'remote', {
        items: remote.items,
        goal: remote.metadata.goal,
        multiplier: remote.metadata.multiplier,
      }),
    );
  }

  private pushForkBackup(backup: ForkBackup): void {
    try {
      const updated = [backup, ...this.readForkBackups()].slice(0, FORK_BACKUP_CAP);
      localStorage.setItem(FORK_BACKUPS_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to write fork backup', error);
    }
  }

  private readForkBackups(): ForkBackup[] {
    try {
      const raw = localStorage.getItem(FORK_BACKUPS_KEY);
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ForkBackup[]) : [];
    } catch (error) {
      console.error('Failed to read fork backups', error);
      return [];
    }
  }

  /**
   * Stashes an entire cross-account quarantine burst (see quarantineForeignOwnedDays) into a
   * dedicated, uncapped bucket — deliberately NEVER the shared, `FORK_BACKUP_CAP`-capped
   * `FORK_BACKUPS_KEY` list that `pushForkBackup` writes to.
   *
   * Every other caller of the fork-backup mechanism stashes at most one day per rare fork event, so
   * the shared cap was sized for that. Quarantine is the first BURST caller: an account switch on a
   * device with a long unsynced local history can quarantine far more than `FORK_BACKUP_CAP` days
   * in a single call. Routing a burst through `pushForkBackup`'s `[backup, ...existing].slice(0,
   * FORK_BACKUP_CAP)` would silently evict — on a device with more than ~10 quarantined days —
   * both earlier days of the SAME burst (once more than the cap have been pushed) and any
   * unrelated, pre-existing fork backups already sitting in that list. The doc comment's "never
   * destroyed outright" would be false exactly at that size.
   *
   * The fix: write the whole burst as backups into a bucket of its own, uncapped, in a single
   * localStorage write. One write (not one per day) also means a burst can never partially evict
   * itself mid-loop. Uncapped is deliberate and safe here: a quarantine burst only ever grows this
   * bucket on an account switch away from unsynced local data, which is rare, and losing any of it
   * is exactly the data-loss this mechanism exists to prevent — unlike ordinary fork backups (which
   * accumulate on every sync conflict and are capped to bound growth), there is no ordinary-usage
   * path that grows this bucket unboundedly.
   */
  private pushQuarantineBackups(backups: ForkBackup[]): void {
    if (backups.length === 0) {
      return;
    }
    try {
      const updated = [...backups, ...this.readQuarantineBackups()];
      localStorage.setItem(QUARANTINE_BACKUPS_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to write quarantine backup', error);
    }
  }

  private readQuarantineBackups(): ForkBackup[] {
    try {
      const raw = localStorage.getItem(QUARANTINE_BACKUPS_KEY);
      if (!raw) {
        return [];
      }
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as ForkBackup[]) : [];
    } catch (error) {
      console.error('Failed to read quarantine backups', error);
      return [];
    }
  }

  // ---- listener plumbing ---------------------------------------------------

  private async attachListener(uid: string, dateKey: string): Promise<void> {
    this.listenerDateKey = dateKey;
    this.lastRemoteToday = null;
    this.listenerCallbackId = await this.firestore.addDocumentSnapshotListener<DocumentData>(
      this.dayRef(uid, dateKey),
      (event, error) => this.onTodaySnapshot(event?.snapshot ?? null, error),
    );
  }

  private async detachListener(): Promise<void> {
    if (this.listenerCallbackId !== null) {
      const id = this.listenerCallbackId;
      this.listenerCallbackId = null;
      try {
        await this.firestore.removeSnapshotListener(id);
      } catch (error) {
        // A plugin rejection here must never abort the caller's cleanup — log and continue.
        console.error('Failed to remove snapshot listener', error);
      }
    }
  }

  // ---- settings sync (last-writer-wins) ------------------------------------

  /**
   * Attaches both directions of settings sync under the current lifecycle generation.
   * LOCAL -> REMOTE via the manual-change subscription; REMOTE -> LOCAL via a snapshot listener.
   * Listener-attach failures are best-effort (logged, non-fatal) so they never abort start().
   */
  private async attachSettingsSync(uid: string): Promise<void> {
    this.settingsSubscription = this.settings.manualUserSettingsChange$.subscribe((settings) =>
      this.syncSettings(settings),
    );
    const gen = this.generation;
    try {
      const callbackId = await this.firestore.addDocumentSnapshotListener<DocumentData>(
        this.settingsRef(uid),
        (event, error) => this.onSettingsSnapshot(event?.snapshot ?? null, error),
      );
      if (gen === this.generation && this.settingsListenerCallbackId === null) {
        this.settingsListenerCallbackId = callbackId;
      } else {
        // Superseded by a stop()/restart while the attach was in flight — remove, don't leak.
        void this.firestore.removeSnapshotListener(callbackId);
      }
    } catch (error) {
      console.error('Sync failed to attach settings listener', error);
    }
  }

  private async detachSettingsListener(): Promise<void> {
    if (this.settingsListenerCallbackId !== null) {
      const id = this.settingsListenerCallbackId;
      this.settingsListenerCallbackId = null;
      try {
        await this.firestore.removeSnapshotListener(id);
      } catch (error) {
        console.error('Failed to remove settings snapshot listener', error);
      }
    }
  }

  /**
   * LOCAL -> REMOTE. Stamps the current settings with a client-clock version and writes them,
   * then advances the local marker so the resulting snapshot echo is recognised and ignored.
   * Public so FirebaseService can delegate its settings write here instead of writing directly.
   */
  syncSettings(settings: UserSettings): void {
    void this.writeSettings(settings);
  }

  private async writeSettings(settings: UserSettings): Promise<void> {
    const uid = this.uid;
    if (!uid) {
      return; // not started — pre-login callers are safe no-ops
    }
    const gen = this.generation;
    const stamp = Date.now();
    const payload: RemoteUserSettings = { ...settings, updatedAt: stamp };
    try {
      await this.firestore.setDocument(this.settingsRef(uid), payload, true);
      if (gen === this.generation) {
        this.settings.setSettingsUpdatedAt(stamp);
      }
    } catch (error) {
      // Best-effort: log and move on. The next manual change re-attempts the write.
      console.error('Sync failed to write settings', error);
    }
  }

  /**
   * REMOTE -> LOCAL. Applies a remote settings doc only when it strictly supersedes what we last
   * applied/sent (updatedAt > marker), with a one-off legacy bootstrap for pre-LWW docs. An echo
   * of our own write (updatedAt === marker) or a stale/older doc is ignored — this kills ping-pong.
   */
  private onSettingsSnapshot(snapshot: DocSnapshot | null, error: unknown): void {
    if (error) {
      console.error('Sync settings snapshot listener error', error);
      return;
    }
    if (!this.uid) {
      return;
    }
    const remote = this.toRemoteSettings(snapshot?.data ?? null);
    if (!remote) {
      return; // no settings doc yet — nothing to apply
    }
    const marker = this.settings.getSettingsUpdatedAt();
    const remoteUpdatedAt = remote.updatedAt ?? 0;

    if (remoteUpdatedAt > marker) {
      this.settings.setUserSettings(this.pickUserSettings(remote));
      this.settings.setSettingsUpdatedAt(remoteUpdatedAt);
      return;
    }
    if (marker === 0 && remoteUpdatedAt === 0 && this.settingsDiffer(remote)) {
      // Legacy-doc bootstrap: a pre-LWW cloud doc must still be adoptable on a fresh device. The
      // marker stays 0 until our first stamped write, so a later stamped remote still wins.
      this.settings.setUserSettings(this.pickUserSettings(remote));
      return;
    }
    // otherwise ignore: own echo (updatedAt === marker) or a stale/older remote.
  }

  private toRemoteSettings(data: DocumentData | null): Partial<RemoteUserSettings> | null {
    if (!data || Object.keys(data).length === 0) {
      return null;
    }
    return data as Partial<RemoteUserSettings>;
  }

  /** Drops the version stamp so only UserSettings fields reach the store. */
  private pickUserSettings(remote: Partial<RemoteUserSettings>): Partial<UserSettings> {
    const copy: Partial<RemoteUserSettings> = { ...remote };
    delete copy.updatedAt;
    return copy;
  }

  /**
   * The single legacy-bootstrap content check (the only place content comparison is allowed as a
   * signal). Order-insensitive equality of the synced settings fields.
   */
  private settingsDiffer(remote: Partial<RemoteUserSettings>): boolean {
    const local = this.settings.getUserSettings();
    const remoteSettings = this.pickUserSettings(remote);
    return (
      JSON.stringify(Object.entries(local).sort()) !==
      JSON.stringify(Object.entries(remoteSettings).sort())
    );
  }

  private settingsRef(uid: string): string {
    return `user_settings/${uid}`;
  }

  // ---- mapping helpers -----------------------------------------------------

  /**
   * Adopts a remote day, first stashing the local version to fork backups when this adoption is a
   * revision REGRESSION — the remote's revision went BACKWARDS below our baseRevision (e.g. an old
   * client overwrote the cloud) and the content differs. Preserves the "nothing destroyed silently"
   * guarantee on that path; an identical-content adoption (own echo) stashes nothing.
   */
  private adoptRemoteMaybeStashing(
    dateKey: string,
    local: LocalDayState | null,
    remote: RemoteDayDoc | null,
  ): void {
    if (
      local &&
      remote &&
      (remote.revision ?? 0) < local.baseRevision &&
      !itemsEqual(local.items, remote.items)
    ) {
      this.stashLocalFork(dateKey, local);
    }
    this.adoptRemote(dateKey, remote);
  }

  /**
   * Adopts a remote day as-is, replacing local state.
   *
   * Known, deliberately accepted limitation: if a phantom day (a local entry mis-filed under a
   * wall-clock day instead of its own logical day — the corruption HistoryRepairService.repair()
   * heals locally) already reached Firestore BEFORE the repair ran on this device, that phantom
   * document keeps being re-adopted here unchanged, indefinitely. Three attempts at correcting this
   * at adopt time (force-write + a persisted queue; dropping the phantom outright; repartitioning
   * the remote document's entries on arrival) each failed review — every one either bypassed
   * `resolveDay` arbitration or risked cross-device/cross-account data loss — so this branch ships
   * without an adopt-time fix rather than ship an unsafe one. New corruption of this shape is
   * structurally impossible going forward (every write path since this branch keys entries by their
   * own timestamp — see HistoryRepository.appendEntry), and the LOCAL copy is still healed by
   * HistoryRepairService regardless of this gap; only a STALE REMOTE document that predates the
   * fix is left uncorrected.
   */
  private adoptRemote(dateKey: string, remote: RemoteDayDoc | null): void {
    if (!remote) {
      return;
    }
    this.history.applyRemoteDay({
      date: dateKey,
      timestamps: remote.items,
      goal: remote.metadata.goal,
      multiplier: remote.metadata.multiplier,
      baseRevision: remote.revision ?? 0,
    });
  }

  private toRemoteDayDoc(dateKey: string, data: DocumentData | null): RemoteDayDoc | null {
    if (!data || Object.keys(data).length === 0) {
      return null;
    }
    const raw = data as Partial<DayDocV2>;
    const metadata = raw.metadata ?? { goal: null, multiplier: 5 };
    return {
      date: typeof raw.date === 'string' ? raw.date : dateKey,
      items: Array.isArray(raw.items) ? raw.items : [],
      metadata: {
        goal: metadata.goal ?? null,
        multiplier: metadata.multiplier ?? 5,
      },
      revision: raw.revision,
      modifiedAt: raw.modifiedAt,
      writerId: raw.writerId,
    };
  }

  private buildLocalDayState(dateKey: string): LocalDayState | null {
    const entity = this.history.getDay(dateKey);
    if (!entity) {
      return null;
    }
    return {
      date: dateKey,
      items: entity.timestamps ?? [],
      goal: entity.goal ?? null,
      multiplier: entity.multiplier,
      baseRevision: entity.baseRevision ?? 0,
      dirty: entity.dirty ?? false,
      modifiedAt: entity.modifiedAt,
      writerId: this.getWriterId(),
    };
  }

  private mintWriterId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `w-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  private dayRef(uid: string, dateKey: string): string {
    return `timer/${uid}/entries/${dateKey}`;
  }

  /** The logical day currently in progress — the same derivation every write path uses. */
  private todayKey(): string {
    return dayKeyFor(new Date(), this.settings.getDayStartHour());
  }
}
